import type { ReactNode } from 'react';

/**
 * The sidebar nav icons, transcribed from `design/PortalDashboard.dc.html`.
 *
 * Nine rows, in the order the artboard draws them. That order is the
 * information architecture of the portal and it is not ours to re-sort. A
 * tenth, the demo row, is not drawn and says so where it is defined, D36.
 *
 * Each icon is the artboard's own `<svg>` contents with one change: every
 * literal stroke colour became `currentColor`, so the row's resting and
 * selected colours come from the row rather than from the icon. Nothing was
 * redrawn. The artboards are the spec (rule 2 and rule 3), and an icon nudged
 * to look better is a design change made in the wrong repo.
 *
 * All of them are drawn on a 24x24 viewBox and rendered at 20x20, which is what
 * every page-2 artboard draws.
 */

const paths: Record<string, ReactNode> = {
  dashboard: (
    <>
      <path
        d="M4 4 H10 V12 H4 Z M14 4 H20 V8 H14 Z M14 12 H20 V20 H14 Z M4 16 H10 V20 H4 Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </>
  ),
  products: (
    <>
      <path
        d="M4 8 L12 4 L20 8 V16 L12 20 L4 16 Z M4 8 L12 12 L20 8 M12 12 V20"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </>
  ),
  'code-batches': (
    <>
      <path
        d="M4 4 H9 V9 H4 Z M15 4 H20 V9 H15 Z M4 15 H9 V20 H4 Z"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path
        d="M15 15 H17.5 V17.5 H15 Z M17.5 17.5 H20 V20 H17.5 Z"
        stroke="currentColor"
        strokeWidth="1.6"
      />
    </>
  ),
  reports: (
    <>
      <path
        d="M4 20 V14 M10 20 V9 M16 20 V12 M22 20 V5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </>
  ),
  'consumer-reports': (
    <>
      <path
        d="M5 21 V4 Q11 1.8 14 4 Q17 6.2 20 4 V14 Q17 16.2 14 14 Q11 11.8 5 14"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  alerts: (
    <>
      <path
        d="M12 3 Q17 3 17 9 V13 L19 16 H5 L7 13 V9 Q7 3 12 3 Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M10 19 Q12 21 14 19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </>
  ),
  team: (
    <>
      <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M3 20 Q3 14 9 14 Q15 14 15 20"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M16 8.5 Q18.5 9 18.5 11.5 M15.5 14.5 Q21 15 21 20"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </>
  ),
  /**
   * Not drawn, D36: the demo row, shown only to a demo-mode org. A scanner's
   * four corners and its line, in the drawn icons' 1.8 stroke.
   */
  'demo-samples': (
    <>
      <path
        d="M4 8 V4 H8 M16 4 H20 V8 M20 16 V20 H16 M8 20 H4 V16 M7 12 H17"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  'api-keys': (
    <>
      <circle cx="8" cy="8" r="4.5" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M11.5 11.5 L20 20 M17 17 L19.5 14.5 M14.5 14.5 L17 12"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </>
  ),
  'brand-profile': (
    <>
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M12 3 V6 M12 18 V21 M3 12 H6 M18 12 H21 M5.6 5.6 L7.8 7.8 M16.2 16.2 L18.4 18.4 M18.4 5.6 L16.2 7.8 M7.8 16.2 L5.6 18.4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </>
  ),
};

export function NavIcon({ name }: { name: string }) {
  const path = paths[name];
  if (!path) return null;
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
      {path}
    </svg>
  );
}
